import { DownloadSVG } from "@components/svgs";
import {
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  useTheme,
} from "@mui/material";
import {
  SHBox,
  SHButton,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { useSelector } from "react-redux";
import { RootState } from "@redux/store";
import { useMemo } from "react";
import { DELAY_TIME } from "@constants";
import { hexToRGBA } from "@utils";
import shadows from "@mui/material/styles/shadows";
import { useToggle } from "react-use";
import { MSExportDialog } from "@pages/managed-accounts/model-screener/components/model-screener-export/dialog";

const TooltipStyled = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "transparent",
    paddingRight: "0px",
  },
  [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
    {
      marginTop: "-1px",
    },
}));

export const ModelScreenerExportButton = () => {
  const { palette } = useTheme();
  const selectedRowRecord = useSelector(
    (state: RootState) => state.modelScreener.selectedRowIds,
  );

  const [isModalOpen, toggleModalOpen] = useToggle(false);

  const selectedRowIds = useMemo(
    () => Object.keys(selectedRowRecord) ?? [],
    [selectedRowRecord],
  );

  const isDisabled = useMemo(
    () => selectedRowIds.length === 0,
    [selectedRowIds],
  );

  const renderTooltipTitle = () => {
    return (
      selectedRowIds.length === 0 && (
        <SHStack
          sx={{
            p: 1,
            mt: "3px",
            backgroundColor: hexToRGBA(palette.common.white, 0.85),
            border: "1px solid #E3E3E3",
            backdropFilter: "blur(2px)",
            borderRadius: "3px",
            boxShadow: shadows[1],
            position: "relative",
          }}
        >
          <SHTypography
            variant="body3"
            sx={{
              p: "5px 5px 0px 10px",
            }}
          >
            Download model, series and{" "}
            <SHBox component={"span"} whiteSpace={"nowrap"}>
              manager-level
            </SHBox>{" "}
            data for selected investments. At least one investment must be
            selected
          </SHTypography>
        </SHStack>
      )
    );
  };

  return (
    <SHStack direction="row" spacing={1} alignItems="center">
      {selectedRowIds.length > 0 && (
        <SHTypography variant="body1" colorVariant="third" lineHeight={1.2}>
          {selectedRowIds.length} selected
        </SHTypography>
      )}

      <TooltipStyled
        placement="bottom"
        enterDelay={DELAY_TIME}
        enterNextDelay={DELAY_TIME}
        title={renderTooltipTitle()}
      >
        <SHBox component={"span"}>
          <SHButton
            disabled={isDisabled}
            variant="contained"
            disableRipple
            sx={{
              "&:active svg path": {
                fill: palette.text.white,
              },
            }}
            startIcon={
              <DownloadSVG width={12} height={13} color={palette.text.white} />
            }
            onClick={() => toggleModalOpen(true)}
          >
            Download
          </SHButton>
        </SHBox>
      </TooltipStyled>

      <MSExportDialog
        isOpen={isModalOpen}
        onClose={() => toggleModalOpen(false)}
      />
    </SHStack>
  );
};
